import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemText } from '@mui/material';
import LoadingSpinner from '../components/LoadingSpinner';
import { SubscriptionModel } from '../interfaces';
import { DELETE_SUBSCRIPTION, GET_SUBSCRIPTION_DELETION, GET_SUBSCRIPTIONS, GET_SUBSCRIPTION_USAGE } from './subscriptions.graphql';

export function DeleteSubscriptionDialog({
  subscription,
  onClose,
  onDeleted,
}: {
  subscription: SubscriptionModel;
  onClose(): void;
  onDeleted(): void;
}) {
  const {
    data,
    loading,
    error: checkError,
  } = useQuery(GET_SUBSCRIPTION_DELETION, {
    variables: { subscriptionId: subscription.bksId },
    fetchPolicy: 'network-only',
  });
  const [deleteSubscription, { loading: deleting }] = useMutation(DELETE_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_SUBSCRIPTIONS }, { query: GET_SUBSCRIPTION_USAGE }],
    awaitRefetchQueries: true,
  });
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setError(null);
    try {
      await deleteSubscription({ variables: { input: { subscriptionId: subscription.bksId } } });
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke slette abonnementet.');
    }
  };

  const deletion = data?.deletion;
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Slett {subscription.name}</DialogTitle>
      <DialogContent>
        {loading && <LoadingSpinner size={40} />}
        {checkError && <Alert severity="error">Fikk ikke sjekket om abonnementet er i bruk.</Alert>}
        {deletion && !deletion.canDelete && (
          <Alert severity="info">
            Abonnementet kan ikke slettes:
            <List dense disablePadding>
              {(deletion.blockers ?? []).map((x) => (
                <ListItem key={x} disableGutters>
                  <ListItemText primary={x} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
        {deletion?.canDelete && (
          <DialogContentText>
            Abonnementet fjernes fra SalgAdmin og BKS.
            {subscription.number && ` Artikkel ${subscription.number} sperres i Maconomy.`} Dette kan ikke angres.
          </DialogContentText>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={deleting} onClick={onClose}>
          {deletion?.canDelete ? 'Avbryt' : 'Lukk'}
        </Button>
        {deletion?.canDelete && (
          <Button variant="contained" color="error" loading={deleting} onClick={remove}>
            Slett
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
